var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { property, query } from 'lit/decorators.js';
import { css, html, LitElement, nothing } from 'lit';
import { stringDecoder, booleanDecoder, submitEventHandler, PIC } from '../Internals';
import { FontAwesomeCSS } from './CSS/FontAwesomeCSS';
import { MaterialDesignCSS } from './CSS/MaterialDesignCSS';
import { PICFontsCSS } from './CSS/PICFontsCSS';
import { ExtraCSS } from './CSS/ExtraCSS';
/**
 * Supported languages available for components.
 * Should be a BCP-47 language tag
 */
export var Languages;
(function (Languages) {
    Languages["EN"] = "en";
    Languages["DK"] = "da";
})(Languages || (Languages = {}));
//TODO interface / class for components that reads data from a url - dataSource
//TODO move input only attributes / functions to PICInput
/**
 * Base class of all WebComponents (made with LitElement)
 */
export class PICBase extends LitElement {
    constructor() {
        super(...arguments);
        /**@description id usable for queryselection, should be unique - Used a [key] when reflecting values back to progress on submit (getFld) */
        this.id_ = '';
        this.eventId_ = '';
        /**@description Whether the component should be disabled or not (readonly mode) */
        this.disabled_ = false;
        /**@description property to help with finding customcomponents with queryselectors */
        this.isPicComp_ = true;
        this.tooltip_ = '';
        /**@description Whether this is a required field */
        this.required_ = false;
        /**@description Wheter this field is readonly */
        this.readonly_ = false;
        /**
         * 	@description Which language to use for translatable parts of the component
         * 	@default Languages.EN
         */
        this.language = Languages.EN;
        this.isDefault = PIC.getInstance.fromProgress;
        this.isLocalClient = !PIC.getInstance.fromProgress && PIC.getInstance.isLocalClient;
        //set to false in constructor to prevent adding to PIC objects input map
        this.isinput__ = true;
        this.fontAwesomeSVG = {
            square: {
                solid: html `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M0 96C0 60.65 28.65 32 64 32H384C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96z"/></svg>`,
                regular: html `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM384 80H64C55.16 80 48 87.16 48 96V416C48 424.8 55.16 432 64 432H384C392.8 432 400 424.8 400 416V96C400 87.16 392.8 80 384 80z"/></svg>`
            },
            squareCheck: html `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM339.8 211.8C350.7 200.9 350.7 183.1 339.8 172.2C328.9 161.3 311.1 161.3 300.2 172.2L192 280.4L147.8 236.2C136.9 225.3 119.1 225.3 108.2 236.2C97.27 247.1 97.27 264.9 108.2 275.8L172.2 339.8C183.1 350.7 200.9 350.7 211.8 339.8L339.8 211.8z"/></svg>`,
            squareMinus: html `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM136 232C122.7 232 112 242.7 112 256C112 269.3 122.7 280 136 280H312C325.3 280 336 269.3 336 256C336 242.7 325.3 232 312 232H136z"/></svg>`
        };
        this.mapSVG = {
            layer: html `<i class="fa fa-layer-group"></i>`,
            download: html `<i class="fa-solid fa-download"></i>`,
            refresh: html `<i class="fa fa-rotate-right"></i>`,
            rotate: html `<i class="fa fa-n"></i>`,
            copyright: html `<i class="fa-solid fa-info"></i>`,
            position: html `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"> <g clip-path="url(#clip0_115_284)"> <path d="M8.00002 5.33332C6.52669 5.33332 5.33335 6.52666 5.33335 7.99999C5.33335 9.47332 6.52669 10.6667 8.00002 10.6667C9.47335 10.6667 10.6667 9.47332 10.6667 7.99999C10.6667 6.52666 9.47335 5.33332 8.00002 5.33332ZM13.96 7.33332C13.6534 4.55332 11.4467 2.34666 8.66669 2.03999V0.666656H7.33335V2.03999C4.55335 2.34666 2.34669 4.55332 2.04002 7.33332H0.666687V8.66666H2.04002C2.34669 11.4467 4.55335 13.6533 7.33335 13.96V15.3333H8.66669V13.96C11.4467 13.6533 13.6534 11.4467 13.96 8.66666H15.3334V7.33332H13.96ZM8.00002 12.6667C5.42002 12.6667 3.33335 10.58 3.33335 7.99999C3.33335 5.41999 5.42002 3.33332 8.00002 3.33332C10.58 3.33332 12.6667 5.41999 12.6667 7.99999C12.6667 10.58 10.58 12.6667 8.00002 12.6667Z" fill="currentColor"/> </g> <defs> <clipPath id="clip0_115_284"> <rect width="16" height="16" fill="white"/> </clipPath> </defs> </svg>`,
            plus: html `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none"> <path fill-rule="evenodd" clip-rule="evenodd" d="M19.0002 13.3874H13.0002V19.3874H11.0002V13.3874H5.00024V11.3874H11.0002V5.38739H13.0002V11.3874H19.0002V13.3874Z" fill="currentColor" /> </svg>`,
            minus: html `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none"> <path fill-rule="evenodd" clip-rule="evenodd" d="M19.0001 15.1875H13.0001H12.4775H11.6729H11.0001H5.00006V13.1875H11.0001H11.4478H12.0001H13.0001H19.0001V15.1875Z" fill="currentColor" /> </svg>`,
            circle: (color = '#66BA60') => { return html `<span class="layerCircle"> <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 8 8" fill="none"> <circle cx="4" cy="4.00003" r="4" fill="${color}"/> </svg> </span>`; }
        };
    }
    /**
     * Return the submit value intended for progress for any given component.
     * Use null for components returning no value
     * @returns a string submit value to be read by web2pro or null to be skipped
     * */
    get submitValue() {
        throw new Error('Method not implemented.');
    }
    static get styles() {
        return css `
        .base-center {
            margin: 0;
            position: absolute;
            top: 50%;
            left: 50%;
            -ms-transform: translate(-50%, -50%);
            transform: translate(-50%, -50%);
        }

        /* fontawesome */
        ${FontAwesomeCSS}    
        /* material design */
        ${MaterialDesignCSS}    
        /* fonts */        
        ${PICFontsCSS}
        /* extra */
        ${ExtraCSS}
        /* webkit browsers  */
        ::-webkit-scrollbar,
        ::-webkit-scrollbar-thumb {
            height: 26px;
            border-radius: 13px;
            background-clip: padding-box;
            /* This makes issues inside shadowroot */
            /* border: 10px solid transparent; */
        }

        ::-webkit-scrollbar{
            width: 6px;
            height: 6px;
        }
        ::-webkit-scrollbar-thumb {        
            box-shadow: inset 0 0 0 10px;
            color: var(--pic-scrollbarPassiveColor);
        }
        ::-webkit-scrollbar-thumb:active,
        ::-webkit-scrollbar-thumb:hover {
            color: var(--pic-scrollbarActiveColor);
        }
        ::-webkit-scrollbar-track,
        ::-webkit-scrollbar-thumb {
            border-radius: 6px;
        }
        ::-webkit-scrollbar-track {
            background-color: var(--pic-scrollbarTrackColor);
        }

        /* Tooltip */
        .pic-tooltip{
            position: relative;
        }
        .pic-tooltip:before{
            content: attr(data-tooltip);
            position: absolute;
            pointer-events: none;

            white-space: pre-wrap;
            word-wrap: break-word;

            font-family: 'Montserrat';
            font-style: normal;
            font-weight: normal;
            font-size: 12px;
            line-height: 16px;
            letter-spacing: 0.4px;
            color: var(--pic-onSurfaceHighColor);
            
            z-index: 5;
            opacity: 0;
            
            top: 50%;
            transform: translateY(-50%);
            
            left: 100%;
            margin-left: 15px;
            
            width: 200px; 
            padding: 10px;
            border-radius: 4px;
            background: var(--pic-primaryColor);
            color: var(--pic-onPrimaryHighColor);
            text-align: center; 
            
            visibility: hidden;
        }
        .pic-tooltip:after{
            content: '';
            position: absolute;
            pointer-events: none;
            
            z-index: 5;
            opacity: 0;
            
            left: 100%;
            margin-left: -5px;
            
            top: 50%;
            transform: translateY(-50%);
            
            border: 10px solid var(--pic-primaryColor);
            border-color: transparent var(--pic-primaryColor) transparent transparent;
            
            visibility: hidden;
        }
        .pic-tooltip:before, .pic-tooltip:after{
            -webkit-transition: all .5s ease-out .5s;
            -moz-transition: all .5s ease-out .5s;
            -ms-transition: all .5s ease-out .5s;
            -o-transition: all .5s ease-out .5s;
            transition: all .5s ease-out .5s;
        }
        .pic-tooltip:hover:not(.picker):before, .pic-tooltip:hover:not(.picker):after{
            visibility: visible;
            opacity: 1;
        }

        .pic-tooltip.left:before{
            left: initial;
            margin: initial;

            right: 100%;
            margin-right: 15px;
        }
        .pic-tooltip.top:before{
            top: initial;
            transform: translateY(50%);
        }
        .pic-tooltip.bottom:before{

        }  
        
        
        .pic-flexgrp {
        display: flex;
        flex-direction: column;
        }

        .pic-flexgrp .row {
        display: flex;
        flex-direction: row;
        gap: 1em;
        }
        .pic-flexgrp .col {
        display: flex;
        flex-direction: column;
        }

        .pic-flexgrp .end {
        align-self: flex-end;
        }

        .pic-flexgrp .start {
        align-self: flex-start;
        }

        .pic-flexgrp .base {
        align-self: baseline;
        }

        .justify-end {
        justify-content: flex-end;
        }

        .justify-center {
        justify-content: center;
        }

        .justify-around {
        justify-content: space-around;
        }
        
        .justify-between {
        justify-content: space-between;
        }
        
        .align-end {
        align-items: flex-end;
        }

        .align-center {
        align-items: center;
        }

        .align-start {
        align-items: flex-start;
        }

        :is(.direction-column) {
        flex-direction: column;
        }   
                
        `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addCMDListeners();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeCMDListeners();
        window.PIC.removeInput(this.id_);
    }
    addCMDListeners() {
        this.addEventListener('disable', this.handleCMDDisable.bind(this));
        this.addEventListener('enable', this.handleCMDEnable.bind(this));
        this.addEventListener('remove', this.handleCMDRemove.bind(this));
        this.addEventListener('cmdfocus', this.handleCMDFocus.bind(this));
    }
    removeCMDListeners() {
        this.removeEventListener('disable', this.handleCMDDisable.bind(this));
        this.removeEventListener('enable', this.handleCMDEnable.bind(this));
        this.removeEventListener('remove', this.handleCMDRemove.bind(this));
        this.removeEventListener('cmdfocus', this.handleCMDFocus.bind(this));
    }
    handleCMDDisable(e) {
        this.disabled_ = true;
        this.requestUpdate();
    }
    handleCMDEnable(e) {
        this.disabled_ = false;
        this.requestUpdate();
    }
    handleCMDRemove(e) {
        this.remove();
    }
    handleCMDFocus(e) {
        this.focus();
    }
    renderCSSLink(link) { return html `<link rel="stylesheet" href=${link}>`; }
    /* This should only be necessary to call outside of xmlprog3 */
    renderCSSLinks() {
        let fontSrc = '/scripts/fonts/picfonts.css';
        let fontLink = this.isDefault
            ? fontSrc
            : this.isLocalClient
                ? 'indexNoPic.css'
                : `.${fontSrc}`;
        let faSrc = '/scripts/fonts/fontawesome-free-6.1.1-web/css/all.min.css';
        let faLink = this.isDefault
            ? faSrc
            : this.isLocalClient
                ? `https://udv.picit.dk${faSrc}`
                : `.${faSrc}`;
        let links = [
            `${fontLink}`,
            `${faLink}`
        ];
        return html `${links.map(lnk => this.renderCSSLink(lnk))}`;
    }
    renderCSS(doRenderCSSLinks) {
        return nothing;
        // return html`
        //     ${doRenderCSSLinks ? this.renderCSSLinks() : ''}
        //     ${this.renderMainCSS()}
        //     ${this.renderCustomCustomerCSS()}
        //     `
    }
    renderMainCSS() {
        let qLink = document.querySelector('head link[href^="/css/picit/webtoolbox.css"]');
        let link = qLink ? qLink.href : '/css/picit/webtoolbox.css';
        if (this.isLocalClient)
            link = `https://udv.picit.dk${link}`;
        return html `${this.renderCSSLink(link)}`;
    }
    renderCustomCustomerCSS() {
        let linkArr = window.location.pathname.split('/');
        let link = `/css/${linkArr[linkArr.length - 1]}/${linkArr[linkArr.length - 1]}.css`;
        let qLink = document.querySelector(`head link[href^="${link}"]`);
        if (!qLink)
            return html ``;
        link = qLink ? qLink.href : link;
        if (link.endsWith('/.css') || link.includes('web.css'))
            return html ``;
        else
            return html `${this.renderCSSLink(link)}`;
    }
    getContainerClassMap() {
        return {
            container: true,
        };
    }
    styleAdder(iStyle, extraStyle, extraStyleValue) {
        let tmpStyle;
        tmpStyle = iStyle;
        if (extraStyle) {
            if (typeof (extraStyle) == 'string')
                tmpStyle[extraStyle] = extraStyleValue;
            else
                tmpStyle = Object.assign(Object.assign({}, tmpStyle), extraStyle);
        }
        return tmpStyle;
    }
    classAdder(iClass, extraClass, extraClassState) {
        let tmpClass;
        tmpClass = iClass;
        if (extraClass) {
            if (typeof (extraClass) == 'string')
                tmpClass[extraClass] = extraClassState ? extraClassState : true;
            else {
                tmpClass = Object.assign(Object.assign({}, tmpClass), extraClass);
            }
        }
        return tmpClass;
    }
    focus() {
        throw new Error('Method not implemented.');
    }
    validateSubmit() {
        throw new Error('Method not implemented.');
    }
    doEvent(e) {
        submitEventHandler(e, this.eventId_);
    }
}
__decorate([
    property({ reflect: true, attribute: 'id' })
], PICBase.prototype, "id_", void 0);
__decorate([
    property({ reflect: true, attribute: 'event' })
], PICBase.prototype, "eventId_", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'disabled', converter: booleanDecoder })
], PICBase.prototype, "disabled_", void 0);
__decorate([
    property({ reflect: true, attribute: 'piccomponent', type: Boolean })
], PICBase.prototype, "isPicComp_", void 0);
__decorate([
    property({ converter: stringDecoder, attribute: 'tooltip' })
], PICBase.prototype, "tooltip_", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'required', converter: booleanDecoder })
], PICBase.prototype, "required_", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'readonly', converter: booleanDecoder })
], PICBase.prototype, "readonly_", void 0);
__decorate([
    property()
], PICBase.prototype, "language", void 0);
__decorate([
    query('.container')
], PICBase.prototype, "containerElement", void 0);
//# sourceMappingURL=PICBase.js.map